import { useState } from 'react';
import PortalLayout from '@/layouts/PortalLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useWebsites } from '@/context/WebsitesContext';
import { Gift, Loader2 } from 'lucide-react';
import ContentView from '@/components/ContentView';
import { supabase } from '@/integrations/supabase/client';

const FreeCredits = () => {
  const { currentWebsite } = useWebsites();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [postThemeId, setPostThemeId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [reopenKey, setReopenKey] = useState(0);
  const [wpPostUrl, setWpPostUrl] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  const handleStart = async () => {
    if (!currentWebsite?.id) return;
    setLoading(true);
    setStatus('');
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      // Fetch preview (best effort)
      try {
        const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/free-credits-post`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          body: JSON.stringify({ website_id: currentWebsite.id, action: 'preview' })
        });
        if (res.ok) {
          const json = await res.json();
          setTitle(json?.preview?.title || `How ${currentWebsite.name || 'we'} use ContentGardener.ai`);
          setContent(json?.preview?.content || '');
        }
      } catch {}

      // Create server-side draft and get post_theme_id
      const draftRes = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/free-credits-post`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ website_id: currentWebsite.id, action: 'draft' })
      });
      const draftJson = await draftRes.json();
      if (!draftRes.ok || !draftJson?.post_theme_id) throw new Error(draftJson?.error || 'Failed to create draft');
      setPostThemeId(draftJson.post_theme_id);
      // Remember last draft to allow reopening later
      try { localStorage.setItem('free_credits_post_theme_id', draftJson.post_theme_id); } catch {}
    } catch (e: any) {
      setStatus(`Error initialising: ${e.message || e}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSendToWordPress = async () => {
    if (!currentWebsite?.id || !postThemeId) { setStatus('No draft created yet. Click Start and open editor.'); return; }
    setIsSending(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      // Publish via existing WordPress flow
      const wpRes = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/wordpress-posts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ website_id: currentWebsite.id, post_theme_id: postThemeId, action: 'create' })
      });
      const wpJson = await wpRes.json();
      if (!wpRes.ok || !wpJson?.success) throw new Error(wpJson?.error || 'Failed to publish');
      if (wpJson?.post?.url) {
        setWpPostUrl(wpJson.post.url);
        try { localStorage.setItem('free_credits_wp_post_url', wpJson.post.url); } catch {}
      }
      setStatus(`Published successfully.`);
    } catch (e: any) {
      setStatus(`Error publishing: ${e.message || e}`);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <PortalLayout>
      <div className="max-w-6xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-medium flex items-center gap-2"><Gift className="h-5 w-5 text-primary"/>Get free monthly credits</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">We’ll create a short post about ContentGardener.ai and your site. You can edit it in the sidebar editor. When you publish to WordPress, we add 5 credits.</p>
            {!postThemeId ? (
              <div className="flex items-center gap-2">
                <Button onClick={handleStart} disabled={loading || !currentWebsite}>
                  {loading ? (<><Loader2 className="h-4 w-4 animate-spin mr-2"/>Preparing…</>) : 'Start and open editor'}
                </Button>
              </div>
            ) : (
              <ContentView
                key={reopenKey}
                postThemeId={postThemeId}
                title={title}
                fullContent={content}
                onClose={() => { /* remain on page - keep reopen button visible */ }}
                onSendToWordPress={handleSendToWordPress}
                isSendingToWP={isSending}
                canSendToWordPress={true}
              />
            )}
            {postThemeId && (
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => setReopenKey(k => k + 1)}>
                  Open editor again
                </Button>
              </div>
            )}
            {postThemeId && (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Post URL on your website (we’ll use this to verify the backlink each month):</p>
                <div className="flex gap-2">
                  <Input
                    placeholder="https://your-site.com/your-article-url"
                    value={wpPostUrl}
                    onChange={(e) => setWpPostUrl(e.target.value)}
                  />
                  <Button
                    variant="secondary"
                    onClick={() => {
                      try {
                        localStorage.setItem('free_credits_wp_post_url', wpPostUrl);
                        setStatus('Saved post URL for monthly backlink checks');
                        // Persist in DB via backlink-rewards enrol
                        (async () => {
                          try {
                            const { data: sessionData } = await supabase.auth.getSession();
                            const token = sessionData?.session?.access_token;
                            await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/backlink-rewards`, {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                ...(token ? { Authorization: `Bearer ${token}` } : {})
                              },
                              body: JSON.stringify({ website_id: currentWebsite?.id, backlink_url: wpPostUrl, action: 'enrol' })
                            });
                          } catch {}
                        })();
                      } catch {}
                    }}
                  >
                    Save link
                  </Button>
                </div>
                {wpPostUrl && (
                  <Button variant="link" className="px-0" onClick={() => window.open(wpPostUrl, '_blank')}>Open post</Button>
                )}
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={async () => {
                      if (!currentWebsite?.id) return;
                      setIsVerifying(true);
                      try {
                        const { data: sessionData } = await supabase.auth.getSession();
                        const token = sessionData?.session?.access_token;
                        const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/backlink-rewards`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            ...(token ? { Authorization: `Bearer ${token}` } : {})
                          },
                          body: JSON.stringify({ website_id: currentWebsite.id, action: 'verify' })
                        });
                        const json = await res.json();
                        if (!res.ok) throw new Error(json?.error || 'Verification failed');
                        setStatus(json?.verified ? 'Backlink found ✔' : 'Backlink not found');
                      } catch (e: any) {
                        setStatus(`Verification error: ${e.message || e}`);
                      } finally {
                        setIsVerifying(false);
                      }
                    }}
                    disabled={isVerifying}
                  >
                    {isVerifying ? (<><Loader2 className="h-4 w-4 animate-spin mr-2"/>Checking…</>) : 'Check now'}
                  </Button>
                </div>
              </div>
            )}
            {status && <div className="text-sm text-muted-foreground">{status}</div>}
          </CardContent>
        </Card>
      </div>
    </PortalLayout>
  );
};

export default FreeCredits;


